import React, { useState, useCallback } from 'react';

// Convert CardSelector to a custom hook
function useCardSelector(cards, onDelete, onDuplicate, onArchive, onExport) {
  const [selectedCardIds, setSelectedCardIds] = useState([]);

  // Check if all selected cards have the same archive state
  const getSelectedCardsArchiveState = useCallback(() => {
    if (selectedCardIds.length === 0) return null;
    
    const selectedCards = cards.filter(card => selectedCardIds.includes(card.id));
    if (selectedCards.length === 0) return null;
    
    const firstCardArchived = selectedCards[0]?.isArchived;
    const allSameState = selectedCards.every(card => card.isArchived === firstCardArchived);
    
    if (!allSameState) return null; // Mixed state
    return firstCardArchived; // true = all archived, false = all unarchived
  }, [selectedCardIds, cards]);

  // Get cards that can be selected based on current selection state
  const getSelectableCards = useCallback(() => {
    const currentState = getSelectedCardsArchiveState();
    
    // If we have a mixed state or no selection, return all cards
    if (currentState === null && selectedCardIds.length === 0) {
      return cards;
    }
    
    // Otherwise, return only cards matching the archive state of current selection
    return cards.filter(card => card.isArchived === currentState);
  }, [getSelectedCardsArchiveState, selectedCardIds.length, cards]);

  // Handle right-click on card
  const handleCardContextMenu = useCallback((e, cardId) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Toggle selection
    if (selectedCardIds.includes(cardId)) {
      setSelectedCardIds(prev => prev.filter(id => id !== cardId));
    } else {
      const clickedCard = cards.find(card => card.id === cardId);
      const currentArchiveState = getSelectedCardsArchiveState();
      
      // If we already have cards selected and they have a consistent archive state
      if (selectedCardIds.length > 0 && currentArchiveState !== null) {
        // Only allow selection if the clicked card matches the archive state of already selected cards
        if (clickedCard.isArchived === currentArchiveState) {
          setSelectedCardIds(prev => [...prev, cardId]);
        } else {
          // Clear previous selection and select only this card
          setSelectedCardIds([cardId]);
        }
      } else {
        // No cards selected yet or mixed state, so just add this one
        setSelectedCardIds(prev => [...prev, cardId]);
      }
    }
  }, [selectedCardIds, cards, getSelectedCardsArchiveState]);

  // Handle select/deselect all
  const toggleSelectAll = useCallback(() => {
    if (selectedCardIds.length === getSelectableCards().length) {
      // Deselect all
      setSelectedCardIds([]);
    } else {
      // Select all cards that match the current view (all archived or all unarchived)
      setSelectedCardIds(getSelectableCards().map(card => card.id));
    }
  }, [selectedCardIds.length, getSelectableCards]);

  // Check if all selectable cards are selected
  const areAllSelected = useCallback(() => {
    const selectableCards = getSelectableCards();
    return selectedCardIds.length === selectableCards.length && selectableCards.length > 0;
  }, [selectedCardIds.length, getSelectableCards]);

  // Handle delete for all selected cards
  const handleDeleteSelected = useCallback(() => {
    const idsToDelete = [...selectedCardIds];
    
    idsToDelete.forEach(id => {
      try {
        onDelete(id);
      } catch (error) {
        console.error(`Error deleting card with ID ${id}:`, error);
      }
    });
    
    setSelectedCardIds([]);
  }, [selectedCardIds, onDelete]);

  // Handle duplicate for all selected cards
  const handleDuplicateSelected = useCallback(() => {
    const idsToDuplicate = [...selectedCardIds];
    
    idsToDuplicate.forEach(id => {
      try {
        onDuplicate(id);
      } catch (error) {
        console.error(`Error duplicating card with ID ${id}:`, error);
      }
    });
  }, [selectedCardIds, onDuplicate]);

  // Handle archive/unarchive for all selected cards
  const handleArchiveSelected = useCallback(() => {
    const idsToProcess = [...selectedCardIds];
    
    idsToProcess.forEach(id => {
      try {
        onArchive(id);
      } catch (error) {
        console.error(`Error archiving/unarchiving card with ID ${id}:`, error);
      }
    });
    
    // Deselect cards after archiving
    setSelectedCardIds([]);
  }, [selectedCardIds, onArchive]);

  // Handle export selected cards
  const handleExportSelected = useCallback(() => {
    if (selectedCardIds.length > 0) {
      const selectedCardsData = cards.filter(card => selectedCardIds.includes(card.id));
      onExport(selectedCardsData);
    }
  }, [selectedCardIds, cards, onExport]);

  // Higher-order component to wrap each card
  const withSelectionWrapper = useCallback((cardElement) => {
    const card = cardElement.props.card;
    const isSelected = selectedCardIds.includes(card.id);
    const currentState = getSelectedCardsArchiveState();
    
    // Determine if this card can be selected based on current selection state
    const isSelectable = 
      selectedCardIds.length === 0 || // No cards selected yet
      currentState === null || // Mixed state
      card.isArchived === currentState; // Matches current selection state
    
    return (
      <div 
        key={card.id}
        className={`card-selection-wrapper ${isSelected ? 'card-selected' : ''} ${!isSelectable ? 'card-not-selectable' : ''}`}
        onContextMenu={(e) => handleCardContextMenu(e, card.id)}
        style={{ position: 'relative' }}
      >
        {isSelected && (
          <div className="selection-indicator">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor" />
            </svg>
          </div>
        )}
        {cardElement}
      </div>
    );
  }, [selectedCardIds, getSelectedCardsArchiveState, handleCardContextMenu]);

  // Render selection toolbar
  const renderSelectionToolbar = useCallback(() => {
    if (selectedCardIds.length === 0) return null;

    // Determine the archive button text based on selection
    const archiveState = getSelectedCardsArchiveState();
    const isArchiveButtonDisabled = archiveState === null; // Disable if mixed state
    const archiveButtonText = archiveState ? "Unarchive" : "Archive";

    return (
      <div className="selection-toolbar">
        <div className="selection-toolbar-left">
          <label className="select-all-container">
            <input 
              type="checkbox" 
              checked={areAllSelected()} 
              onChange={toggleSelectAll} 
              className="select-all-checkbox" 
            />
            <span className="checkmark"></span>
            <span className="selection-count">{selectedCardIds.length} card{selectedCardIds.length !== 1 ? 's' : ''} selected</span>
          </label>
        </div>
        <div className="selection-toolbar-right">
          <button 
            onClick={handleDuplicateSelected} 
            className="selection-action-button"
          >
            Duplicate
          </button>
          <button 
            onClick={handleExportSelected} 
            className="selection-action-button"
          >
            Export
          </button>
          <button 
            onClick={handleArchiveSelected} 
            className="selection-action-button"
            disabled={isArchiveButtonDisabled}
          >
            {archiveButtonText}
          </button>
          <button 
            onClick={handleDeleteSelected} 
            className="selection-action-button delete-button"
          >
            Delete
          </button>
        </div>
      </div>
    );
  }, [
    selectedCardIds, 
    getSelectedCardsArchiveState, 
    areAllSelected, 
    toggleSelectAll, 
    handleDuplicateSelected, 
    handleExportSelected, 
    handleArchiveSelected,
    handleDeleteSelected // Added this missing dependency
  ]);

  // Function to handle card archive state changes
  const onCardArchiveChange = useCallback((cardId) => {
    if (selectedCardIds.includes(cardId)) {
      setSelectedCardIds(prev => prev.filter(id => id !== cardId));
    }
  }, [selectedCardIds]);

  // Return the selection API
  return {
    withSelectionWrapper,
    renderSelectionToolbar,
    selectedCount: selectedCardIds.length,
    clearSelection: () => setSelectedCardIds([]),
    onCardArchiveChange
  };
}

export default useCardSelector;